import React from 'react';
import LegalLayout, { H2, P, UL } from '../components/legal/LegalLayout';

// Privacy & Data Safety Policy — wording VERBATIM from the official document.

const TABLE_ROWS = [
  ['Google Play Billing', 'Mobile subscription processing and user plan management.', 'Account tier status, confirmation receipt numbers, encryption tokens.'],
  ['Crossmint Inc.', 'Immutable minting of digital certificate badges ("NFT milestones").', 'Programmatic public keys, badge metadata identifiers. No minor billing or real identity details are shared.'],
  ['Stripe Inc.', 'Safe processing of web-based parent billing accounts.', 'Transaction metadata, billing addresses, name fields.'],
  ['Cloud Hosts', 'Highly secure server database operations and monitoring.', 'Multi-layer encrypted operational logs and asset maps.'],
];

export default function PrivacyPage() {
  return (
    <LegalLayout title="PRIVACY & DATA SAFETY POLICY" lastUpdated="September 11, 2026" testId="privacy-page">
      <H2 id="1">1. INTRODUCTION</H2>
      <P>HiBFF Ltd ("we", "our", "us") operates the website hibff.com and the HiBFF mobile application (collectively, the "Platform"). The Platform provides a parent-supervised, safe space for teenagers (ages 13–17) to build confidence.</P>
      <P>We are fully committed to protecting the privacy of our users. This policy outlines our compliance with the General Data Protection Regulation (GDPR), UK GDPR, California Consumer Privacy Act (CCPA), Children's Online Privacy Protection Act (COPPA), and Google Play Developer Program Policies.</P>

      <H2 id="2">2. DATA CONTROLLER</H2>
      <P>HiBFF Ltd is the data controller for personal data collected through our platform.</P>
      <UL items={[
        <>Contact Email: <a href="mailto:privacy@hibff.com" className="underline text-blue-700">privacy@hibff.com</a> / <a href="mailto:support@hibff.com" className="underline text-blue-700">support@hibff.com</a></>,
        'Corporate Address: HiBFF Ltd, 71-75 Shelton Street, Covent Garden, London, WC2H 9JQ, United Kingdom',
      ]} />

      <H2 id="3">3. STRICT PARENTAL OVERSIGHT & ROLE ISOLATION</H2>
      <P>The Platform enforces a strict programmatic structural split between adult parent/guardian accounts and minor child profiles (ages 13–17).</P>
      <UL items={[
        <><strong>Teen Profiles (Ages 13–17):</strong> Teen users interact with localized educational confidence exercises. Teen accounts are completely decoupled from primary commercial layers. They have no access to transaction triggers, purchase options, or blockchain minting engines inside the app client.</>,
        <><strong>Parent Dashboards:</strong> Parent/Guardian profiles retain exclusive authorization control over all premium subscriptions, user onboarding setups, and tokenized asset allocations.</>,
      ]} />

      <H2 id="4">4. INTEGRATED PAYMENT AND TRANSACTIONS SYSTEMS</H2>
      <P>We do not collect or store raw credit/debit card data on our servers. All subscription monetization is securely externalized across our two operational environments:</P>
      <UL items={[
        <><strong>Android Application Subscriptions:</strong> All payment tokenization, premium plans, and active billing histories inside our mobile build are handled exclusively by the secure Google Play Billing System.</>,
        <><strong>Web Interface Transactions:</strong> Web-based subscription infrastructure and credit card processing are managed securely via Stripe Payments Europe Ltd.</>,
      ]} />

      <H2 id="5">5. WHAT PERSONAL DATA WE COLLECT</H2>
      <P>Every category of collected data matches our official Google Play Data Safety Declaration form.</P>
      <P><strong>A. Data Provided Directly</strong></P>
      <UL items={[
        <><strong>Identity and Contact:</strong> Account name, username, billing country, and verified email address.</>,
        <><strong>Parental Management Logs:</strong> Cross-referenced parent email verification files ensuring authorized registration loops.</>,
        <><strong>Milestone Analytics:</strong> Record of completed confidence exercises chosen by teens and subsequently approved by parents.</>,
      ]} />
      <P><strong>B. Technical and Device Data (Collected Automatically)</strong></P>
      <UL items={[
        <><strong>System Identifiers:</strong> IP addresses, device hardware models, operating system variants, platform access tracking records, and localized app activity timestamps.</>,
      ]} />

      <H2 id="6">6. DATA SHARING & THIRD-PARTY BLOCKCHAIN UTILITIES</H2>
      <P>We do not sell personal user information under any circumstances. Data sharing is strictly restricted to infrastructural ecosystem utilities necessary to deliver our features:</P>
      <div className="overflow-x-auto mb-3" data-testid="privacy-sharing-table">
        <table className="w-full text-sm text-left text-gray-700 border border-gray-200 rounded-lg">
          <thead className="bg-gray-100 text-gray-900 font-bold">
            <tr>
              <th className="px-3 py-2 border-b border-gray-200">Recipient</th>
              <th className="px-3 py-2 border-b border-gray-200">Purpose</th>
              <th className="px-3 py-2 border-b border-gray-200">Data Transmitted / Scope</th>
            </tr>
          </thead>
          <tbody>
            {TABLE_ROWS.map(([r, p, d]) => (
              <tr key={r} className="align-top odd:bg-white even:bg-gray-50">
                <td className="px-3 py-2 border-b border-gray-100 font-semibold whitespace-nowrap">{r}</td>
                <td className="px-3 py-2 border-b border-gray-100">{p}</td>
                <td className="px-3 py-2 border-b border-gray-100">{d}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <H2 id="7">7. BLOCKCHAIN-BASED CONTENT DEFENSES</H2>
      <P>In complete alignment with Google Play’s Blockchain-based Content Framework, the Platform operates a non-monetized digital tokenization layout:</P>
      <UL items={[
        <><strong>No Randomization ("Loot Boxes"):</strong> The acquisition of tokenized digital assets is completely deterministic and linear. No digital assets of unknown or randomized value are issued.</>,
        <><strong>No Speculative Marketing:</strong> We strictly evaluate and display digital milestone rewards as badges of motivational personal growth. We do not facilitate, promote, or encourage commercial marketplace trading or asset speculation within our mobile code.</>,
      ]} />

      <H2 id="8">8. USER RIGHTS AND RETENTION SCHEDULES</H2>
      <P>Under GDPR, UK GDPR, and CCPA standards, users (and their verified parents) maintain the right to view, download, update, or completely purge their personal profile history.</P>
      <UL items={[
        <><strong>Data Erasure:</strong> To permanently delete account entries, contact us directly at <a href="mailto:privacy@hibff.com" className="underline text-blue-700">privacy@hibff.com</a>.</>,
        <><strong>Retention Boundaries:</strong> Standard operational security and profile log histories are auto-purged within a maximum of 12 months, unless financial taxation records or legal compliance protocols mandate a extended legal holding period.</>,
      ]} />

      {/* Carrier-required SMS disclosure (A2P 10DLC / CTIA) — must remain
          crawlable HTML on this page for Twilio campaign verification. */}
      <div className="mt-10 pt-8 border-t border-gray-200" data-testid="legal-sms-privacy-section">
        <h2 className="text-lg font-bold text-gray-900 mb-3">SMS / Text Messaging Privacy</h2>
        <P>
          No mobile information will be shared with third parties or affiliates for marketing or
          promotional purposes. Information sharing with subcontractors in support services, such as
          customer service, is permitted. All other use-case categories exclude text-messaging
          originator opt-in data and consent; this information will not be shared with, or sold to,
          any third parties.
        </P>
        <P>
          When you opt in to SMS on our signup form, we record your phone number, the consent text
          displayed, the date and time, and your IP address solely as evidence of your consent. You
          can withdraw consent at any time by replying STOP to any message.
        </P>
      </div>
    </LegalLayout>
  );
}
